import { useCallback, useEffect, useRef, useState } from 'react';

import { BannerTypingIA } from './components/banner-typing-ia';
import { Composer } from './components/Composer';
import { Header } from './components/Header';
import { MessageList } from './components/MessageList';
import { PainelCenario } from './components/PainelCenario';
import { useSimuladorClient } from './hooks/use-simulador-client';
import {
  fetchConversationMessages,
  fetchOwnConversationId,
  seedWorld,
  sendInboundMessage,
} from './lib/api';
import { actionForEvent, isOwnConversation } from './lib/event-conversation';
import {
  loadStoredConversationId,
  reconcileStoredScenario,
  saveConversationId,
} from './lib/session-storage';

import type { ChatMessage, MessageReceivedData, MessageSentData, WorldSpec } from './lib/types';

const DEFAULT_TENANT = 'tenant_netcomet';
// ★ O canal que este app GRAVA. `simulador`, não `webchat`: é este literal que decide o
// `channel` da `Conversation` criada por `POST /_mock/inbound-message`. Enquanto ele dizia
// `webchat`, abrir o simulador recriava linhas do canal de produção — e a migração
// `20260819010000_laboratorios_para_canal_simulador` decairia em silêncio a cada uso.
const CHANNEL_KIND = 'simulador';

/**
 * Harness de auditoria MAC (DEV-ONLY): `?tenant=` e `?cid=` apontam o cliente dev a um
 * tenant + channelIdentifier específicos (ex.: a conversa proativa semeada por
 * `/_mock/seed-scenario`, que fala só com endpoints `@AssertDevOnly`). Travado em
 * `import.meta.env.DEV` → IMPOSSÍVEL num build de produção (não expõe seleção de tenant
 * nem impersonação de identidade por query param). O cliente Webchat de produção
 * resolverá tenant via env/rota e identidade via a sessão do canal — NUNCA por query param.
 * Sem query params (ou em produção), mantém o comportamento mock v1.
 */
function initialTenant(): string {
  if (import.meta.env.DEV) {
    const q = new URLSearchParams(window.location.search).get('tenant');
    if (q !== null && q.length > 0) return q;
  }
  return DEFAULT_TENANT;
}

/**
 * O cenário já veio de fora? O harness MAC semeia por `/_mock/seed-scenario` e passa o
 * identificador em `?cid=` — nesse caso pedir outro cenário seria semear duas vezes.
 */
function veioComCidNaUrl(): boolean {
  if (!import.meta.env.DEV) return false;
  const q = new URLSearchParams(window.location.search).get('cid');
  return q !== null && q.length > 0;
}

function defaultChannelIdentifier(): string {
  if (import.meta.env.DEV) {
    const q = new URLSearchParams(window.location.search).get('cid');
    if (q !== null && q.length > 0) return q;
  }
  const stored = window.localStorage.getItem('lume-simulador-mock-id');
  if (stored !== null && stored.length > 0) return stored;
  const generated = `mock-simulador-${String(Math.random()).slice(2, 10)}`;
  window.localStorage.setItem('lume-simulador-mock-id', generated);
  return generated;
}

export function App(): JSX.Element {
  const [tenantId, setTenantId] = useState(initialTenant);
  const [channelIdentifier, setChannelIdentifier] = useState(defaultChannelIdentifier);
  /**
   * Cenário preparado. `null` ⇒ o painel pede um antes de abrir a conversa.
   *
   * ⚠️ Só em DEV, e a razão não é estética: preparar mundo é operação de laboratório, e o
   * endpoint que a executa é bloqueado em produção pelo próprio engine. Em produção este estado
   * nasce «já resolvido» (`chatDireto`) e o app se comporta como antes — o cliente Webchat de
   * produção resolverá tenant e identidade pela sessão do canal, nunca por um painel.
   *
   * Exceção declarada: com `?cid=` na URL o cenário já veio de fora (o harness MAC semeia e
   * passa o identificador), então não se pede outro — seria pedir para semear duas vezes.
   */
  const [cenario, setCenario] = useState<{
    channelIdentifier: string;
    world: WorldSpec;
  } | null>(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);
  const chatDireto = !import.meta.env.DEV || veioComCidNaUrl();
  // Item 275: conversationId recovery — reload preserva ponto da conversa ativa.
  // Histórico mensagens NÃO persiste (out of scope mock harness v1).
  // A-21 (noturna 04/Set/2026): a conversa salva só vale para o MESMO `cid`. Com `?cid=` novo,
  // o Simulador reabria a conversa do cenário anterior e contaminava a validação viva.
  const [conversationId, setConversationId] = useState<string | null>(() => {
    reconcileStoredScenario(channelIdentifier);
    return loadStoredConversationId();
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const knownMessageIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (conversationId !== null) saveConversationId(conversationId, channelIdentifier);
  }, [conversationId]);

  /**
   * ★★ SEC-001 — o evento é AVISO, o REST é ENTREGA. Não leia `content` daqui.
   *
   * O broadcast de tenant do engine ia para TODO operador conectado com o texto da
   * mensagem dentro, furando o ACL que o REST aplica. O `content` saiu do payload — então
   * este handler faz o que o `lume-atendimento` e o app móvel já faziam: recebe o aviso e
   * refaz o fetch.
   *
   * ⚠️ A 1ª tentativa daquela correção foi REVERTIDA porque ESTE arquivo lia
   * `data.content` e não tinha fallback nenhum: a IA respondia e a tela ficava em branco.
   * É a mesma classe do incidente de 19/Ago (vide o docblock do `handleMessageSent`
   * abaixo): o emissor existia, o listener existia, e o vínculo entre os dois não.
   * **Se você for ler algum campo de conteúdo de um evento de WS aqui, pare.**
   */
  const sincronizarMensagens = useCallback(
    async (convId: string) => {
      try {
        const { data } = await fetchConversationMessages({ tenantId, conversationId: convId });
        // A busca é a fonte; o `knownMessageIdsRef` segue existindo para o caminho de
        // `message.sent`, que ainda entrega direto (mensagem de sistema).
        data.forEach((m) => knownMessageIdsRef.current.add(m.id));
        setMessages(
          data.map((m) => ({
            id: m.id,
            origin: m.origin as ChatMessage['origin'],
            content: m.content,
            timestamp: m.sentAt,
          })),
        );
      } catch {
        // Falha de rede não pode apagar a tela: mantém o que já está renderizado. O
        // próximo evento tenta de novo.
      }
    },
    [tenantId],
  );

  /**
   * Reload com conversa ativa passa a RECUPERAR o histórico.
   *
   * Antes isto era inevitável e estava declarado no Item 275 («histórico de mensagens NÃO
   * persiste»): o `conversationId` sobrevivia ao reload e a tela voltava vazia, porque a
   * única fonte do conteúdo eram os eventos já passados. Com a busca do SEC-001 existindo,
   * a limitação deixou de ter motivo — quem recarrega no meio de um teste continua de onde
   * estava em vez de perder o fio.
   */
  useEffect(() => {
    if (conversationId === null) return;
    void sincronizarMensagens(conversationId);
    // Só na montagem/reconexão de conversa: os eventos cuidam do resto.
    // (Sem `eslint-disable` de `react-hooks/exhaustive-deps`: nenhum eslint.config deste
    // repo registra o plugin `react-hooks`, então a diretiva era INERTE e o próprio lint
    // a recusava — «Definition for rule ... was not found». Achado na integração 23/Ago.)
  }, []);

  /**
   * Item 515 — o evento é do tenant inteiro e não diz de quem é a conversa. A da tela sincroniza; uma
   * desconhecida só vira a da tela se o engine confirmar que é a mais recente DESTE cliente (a IA que
   * fala primeiro, ou a conversa nova depois de uma encerrada). Antes, com a tela sem conversa, o
   * primeiro evento a definia — e podia ser o de outra pessoa do mesmo tenant.
   */
  const handleEventConversation = useCallback(
    (eventConversationId: string) => {
      const action = actionForEvent(conversationId, eventConversationId);
      if (action.kind === 'sync') {
        void sincronizarMensagens(action.conversationId);
        return;
      }
      void (async () => {
        const own = await fetchOwnConversationId({ tenantId, channelIdentifier });
        if (!isOwnConversation(own, action.conversationId)) return;
        setConversationId(action.conversationId);
        await sincronizarMensagens(action.conversationId);
      })();
    },
    [conversationId, tenantId, channelIdentifier, sincronizarMensagens],
  );

  const handleMessageReceived = useCallback(
    (data: MessageReceivedData) => {
      handleEventConversation(data.conversationId);
    },
    [handleEventConversation],
  );

  const cenarioPronto = chatDireto || cenario !== null;

  /**
   * ★ Mensagem de SISTEMA (handoff, avisos) chega por `conversation.message.sent`, não por
   * `message.received` — caminhos diferentes no engine (`sendOutboundSystem` × resposta da IA).
   *
   * ⚠️ Este callback FALTAVA, e o custo era a mensagem inteira. Medido em 19/Ago/2026 na
   * validação viva: o porteiro decidiu handoff, o engine gravou e emitiu «Esse pedido exige uma
   * confirmação de identidade… já te passei para um atendente», e **a tela não mostrou nada** —
   * da cadeira do cliente a IA pediu o CPF e emudeceu. O emissor existia, o `socket.on` existia,
   * e o vínculo entre os dois não: contar a ARESTA, não só os nós.
   *
   * Dedup pelo mesmo `knownMessageIdsRef`: se algum dia um caminho emitir os DOIS eventos para a
   * mesma mensagem, ela aparece uma vez.
   */
  const handleMessageSent = useCallback(
    (data: MessageSentData) => {
      // ★★ SEC-001 — este caminho TAMBÉM é aviso, não entrega. Não leia `content` daqui.
      //
      // ⚠️ Eu errei exatamente aqui, e só a validação VIVA pegou. Reescrevi o
      // `handleMessageReceived` para refazer o fetch e ESQUECI este. Os testes ficaram
      // verdes, a 1ª resposta da IA apareceu — e a 2ª, que vem por `message.sent`,
      // renderizou uma bolha VAZIA. É o mesmo sintoma do incidente de 19/Ago descrito no
      // docblock acima, na mesma linha de código, pela segunda vez.
      //
      // A lição não é «tome cuidado»: é que redigir um payload obriga a enumerar TODOS os
      // handlers que o leem, não o primeiro que vem à cabeça.
      handleEventConversation(data.conversationId);
    },
    [handleEventConversation],
  );

  const { state, emitClientTyping } = useSimuladorClient({
    tenantId,
    label: channelIdentifier,
    onMessageReceived: handleMessageReceived,
    onMessageSent: handleMessageSent,
    // Gatear o FETCH, não só o render: sem cenário não se abre sessão nem WS. Conectar antes
    // criaria a Identity do mundo ERRADO — e é ela que o cenário existe para escolher.
    enabled: cenarioPronto,
  });

  async function handleSeed(world: WorldSpec): Promise<void> {
    setSeedBusy(true);
    setSeedError(null);
    try {
      const handle = await seedWorld(world);
      // O mundo vive num tenant PRÓPRIO de laboratório (decisão do TL, 19/Ago): o semeador muta
      // config de nível de tenant, e num tenant de trabalho isso corromperia a configuração.
      setTenantId(handle.tenantId);
      setChannelIdentifier(handle.channelIdentifier);
      setCenario({ channelIdentifier: handle.channelIdentifier, world: handle.declaredWorld });
      setMessages([]);
      knownMessageIdsRef.current = new Set();
      setConversationId(handle.conversationId);
    } catch (err) {
      setSeedError(err instanceof Error ? err.message : String(err));
    } finally {
      setSeedBusy(false);
    }
  }

  function handleResetCenario(): void {
    setCenario(null);
    setMessages([]);
    knownMessageIdsRef.current = new Set();
    setConversationId(null);
  }

  async function handleSend(text: string): Promise<void> {
    try {
      // Sem optimistic outbound — WS event `conversation.message.received`
      // chega em ~50ms via dual emit do Engine + dedup canonical via
      // knownMessageIdsRef. Evita duplicação visual no smoke harness.
      const result = await sendInboundMessage({
        tenantId,
        channel: CHANNEL_KIND,
        channelIdentifier,
        content: text,
        displayName: 'Cliente do Simulador',
      });
      if (conversationId === null) {
        setConversationId(result.conversationId);
        // Item 515 — os eventos que chegaram antes desta resposta foram ignorados (a tela ainda não
        // sabia a conversa); busca uma vez para a própria mensagem aparecer sem esperar a da IA.
        void sincronizarMensagens(result.conversationId);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessages((prev) => [
        ...prev,
        {
          id: `err_${String(Date.now())}`,
          origin: 'system',
          content: `Falha ao enviar: ${msg}`,
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  }

  function handleTypingStart(): void {
    if (conversationId === null) return;
    emitClientTyping(conversationId, 'started');
  }

  function handleTypingStop(): void {
    if (conversationId === null) return;
    emitClientTyping(conversationId, 'stopped');
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'stretch',
        height: '100vh',
        padding: '24px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '480px',
          background: 'var(--lume-panel)',
          border: '1px solid var(--lume-border)',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Header status={state.status} tenantId={tenantId} conversationId={conversationId} />
        {!chatDireto && (
          <PainelCenario
            onSeed={handleSeed}
            busy={seedBusy}
            error={seedError}
            ativo={cenario}
            onReset={handleResetCenario}
          />
        )}
        {cenarioPronto && <MessageList messages={messages} />}
        <BannerTypingIA visible={state.isAiTyping} />
        {state.error !== null && (
          <div
            style={{
              padding: '8px 16px',
              background: '#fef3c7',
              color: '#92400e',
              fontSize: '12px',
              borderTop: '1px solid #fcd34d',
            }}
            role="alert"
          >
            {state.error}
          </div>
        )}
        {cenarioPronto && (
          <Composer
            disabled={state.status !== 'connected'}
            onSend={(text) => {
              void handleSend(text);
            }}
            onTypingStart={handleTypingStart}
            onTypingStop={handleTypingStop}
          />
        )}
      </div>
    </div>
  );
}
